/**
 * CDF (Combined Dual Fuel) Email Support Utilities
 *
 * This module provides utilities for detecting and handling CDF email templates
 * which require special multi-fuel type handling in the CX Template Editor.
 */

/**
 * Tag value used to identify configs/strings that should be duplicated per fuel type
 * This tag appears in the `attributes` array
 */
export const CDF_EL_GAS_WATER_TAG = 'CDF_EL_GAS_WATER';

/**
 * Detect if a template is a CDF template based on template key or product type
 *
 * @param templateKey - The template key (e.g., "EMAIL.CDF.MONTHLY_SUMMARY")
 * @param productType - Optional product type from parsed template key
 * @returns true if this is a CDF template
 *
 * @example
 * isCdfTemplate('EMAIL.CDF.MONTHLY_SUMMARY') // true
 * isCdfTemplate('EMAIL.DF.USER_WELCOME') // false
 */
export function isCdfTemplate(templateKey: string, productType?: string): boolean {
  if (!templateKey) return false;

  // Check if template key contains CDF indicator
  const upperKey = templateKey.toUpperCase();
  return upperKey.includes('.CDF.') ||
         upperKey.includes('_CDF_') ||
         productType === 'CDF';
}

/**
 * Check if an item (config or string resource) should be duplicated per fuel type
 * Checks if the attributes array contains the CDF_EL_GAS_WATER tag
 *
 * @param attributes - Array of attribute tags from API response
 * @returns true if item should be shown once per supported fuel type
 *
 * @example
 * shouldDuplicateForFuels(['CDF_EL_GAS_WATER', 'OTHER_TAG']) // true - duplicates because of CDF_EL_GAS_WATER
 * shouldDuplicateForFuels(['Gas', 'CDF_EL_GAS_WATER']) // true - duplicates only because of CDF_EL_GAS_WATER tag
 * shouldDuplicateForFuels(['Gas']) // false - Gas alone doesn't trigger duplication
 * shouldDuplicateForFuels(['STANDARD_TAG']) // false
 * shouldDuplicateForFuels(undefined) // false
 */
export function shouldDuplicateForFuels(attributes?: string[] | null): boolean {
  if (!attributes || !Array.isArray(attributes)) return false;

  return attributes.includes(CDF_EL_GAS_WATER_TAG);
}

/**
 * Create a fuel-specific ID for a duplicated item
 * 
 * @param originalId - The original config key or string resource ID
 * @param fuelType - The fuel type (ELECTRIC, GAS, WATER)
 * @returns Fuel-specific ID with separator
 * 
 * @example
 * createFuelSpecificId('com.bidgely.config.key', 'ELECTRIC') 
 * // 'com.bidgely.config.key__ELECTRIC'
 */
export function createFuelSpecificId(originalId: string, fuelType: string): string {
  return `${originalId}__${fuelType}`;
}



/**
 * Create fuel-specific display name
 * 
 * @param displayName - Original display name
 * @param fuelType - Fuel type to append
 * @returns Display name with fuel type suffix
 * 
 * @example
 * createFuelSpecificDisplayName('Total Spent', 'ELECTRIC')
 * // 'Total Spent - ELECTRIC'
 */
export function createFuelSpecificDisplayName(displayName: string, fuelType: string): string {
  return `${displayName} - ${fuelType}`;
}

/**
 * Metadata attached to duplicated items to track original ID and fuel type
 */
export interface CdfItemMetadata {
  /** Original config key or string resource ID (before fuel suffix) */
  originalId: string;
  /** Fuel type this instance represents */
  fuelType: string;
  /** Attributes from API (e.g., ["CDF_EL_GAS_WATER", "OTHER_TAG"]) */
  attributes?: string[];
}

/** Maps fuel-specific attribute tags to their canonical fuel type strings */
const FUEL_ATTRIBUTE_MAP: Record<string, string> = {
  'Electric': 'ELECTRIC',
  'Gas': 'GAS',
  'Water': 'WATER',
};

const FUEL_SPECIFIC_ATTRIBUTES = Object.keys(FUEL_ATTRIBUTE_MAP);

/**
 * Check if a string resource is compatible with the current fuel type
 * based on its fuel-specific attributes (Electric, Gas, Water).
 *
 * Returns true (show the item) when:
 *  - No fuel-specific attribute is present (no restriction)
 *  - currentFuelType is empty (no fuel context — e.g. CDF or no-fuel templates)
 *  - The item's fuel attribute matches currentFuelType
 *
 * Returns false (hide the item) when the item is tagged for a different fuel.
 *
 * @param attributes - Array of attribute tags from API response
 * @param currentFuelType - Active fuel type in the inspector (ELECTRIC, GAS, WATER, or '' for all)
 *
 * @example
 * isFuelCompatible(['Gas'], 'ELECTRIC') // false — gas-only string hidden for electric view
 * isFuelCompatible(['Gas'], 'GAS')      // true  — gas-only string shown for gas view
 * isFuelCompatible(['Gas'], '')          // true  — no fuel context, show all
 * isFuelCompatible(undefined, 'GAS')         // true  — no restriction
 */
export function isFuelCompatible(attributes: string[] | undefined | null, currentFuelType: string): boolean {
  if (!attributes || !Array.isArray(attributes)) return true;
  if (!currentFuelType) return true;

  const hasFuelSpecificAttr = attributes.some(attr => FUEL_SPECIFIC_ATTRIBUTES.includes(attr));
  if (!hasFuelSpecificAttr) return true;

  return attributes.some(attr => FUEL_ATTRIBUTE_MAP[attr] === currentFuelType.toUpperCase());
}


